import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Pencil,
  Trash2,
  Phone,
  Briefcase,
  Zap,
  Radio,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_FLOW = ["new", "contacted", "meeting_fixed", "converted", "lost"];

const statusConfig = {
  new: {
    label: "New",
    badge: "bg-blue-50 text-blue-700 border-blue-200",
    dot: "bg-blue-500",
    bar: "bg-blue-400",
  },
  contacted: {
    label: "Contacted",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
    bar: "bg-amber-400",
  },
  meeting_fixed: {
    label: "Meeting Fixed",
    badge: "bg-violet-50 text-violet-700 border-violet-200",
    dot: "bg-violet-500",
    bar: "bg-violet-400",
  },
  converted: {
    label: "Converted",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    bar: "bg-emerald-400",
  },
  lost: {
    label: "Lost",
    badge: "bg-red-50 text-red-700 border-red-200",
    dot: "bg-red-400",
    bar: "bg-red-400",
  },
};

const nextAction = {
  new: {
    label: "Mark Contacted",
    next: "contacted",
    testId: "mark-contacted-button",
  },
  contacted: {
    label: "Fix Meeting",
    next: "meeting_fixed",
    testId: "mark-meeting-fixed-button",
  },
  meeting_fixed: {
    label: "Mark Converted",
    next: "converted",
    testId: "mark-converted-button",
  },
};

const EMPTY_FORM = {
  name: "",
  phone: "",
  business: "",
  requirement: "",
  source: "",
  assigned_pillar: "",
};

function LeadCardSkeleton() {
  return (
    <Card className="p-5 lg:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="flex gap-1 mt-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-1.5 flex-1 rounded-full" />
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-28 rounded-lg" />
          <Skeleton className="h-8 w-8 rounded-lg" />
          <Skeleton className="h-8 w-8 rounded-lg" />
        </div>
      </div>
    </Card>
  );
}

function StatusProgressBar({ status }) {
  const idx = STATUS_FLOW.indexOf(status);
  return (
    <div className="flex gap-1 mt-3">
      {STATUS_FLOW.map((s, i) => {
        const cfg = statusConfig[s];
        const filled = i <= idx;
        return (
          <div
            key={s}
            title={statusConfig[s].label}
            className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
              filled ? cfg.bar : "bg-muted"
            }`}
          />
        );
      })}
    </div>
  );
}

function LeadFormDialog({
  open,
  onOpenChange,
  initial,
  onSubmit,
  title,
  submitLabel,
  loading,
}) {
  const [form, setForm] = useState(initial ?? EMPTY_FORM);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    setForm(initial ?? EMPTY_FORM);
  }, [initial, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Fill in the lead details below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                data-testid="lead-name-input"
                value={form.name}
                onChange={set("name")}
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                data-testid="lead-phone-input"
                value={form.phone}
                onChange={set("phone")}
                required
              />
            </div>
            <div>
              <Label htmlFor="source">Source *</Label>
              <Input
                id="source"
                data-testid="lead-source-input"
                value={form.source}
                onChange={set("source")}
                required
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="business">Business *</Label>
              <Input
                id="business"
                data-testid="lead-business-input"
                value={form.business}
                onChange={set("business")}
                required
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="requirement">Requirement *</Label>
              <Input
                id="requirement"
                data-testid="lead-requirement-input"
                value={form.requirement}
                onChange={set("requirement")}
                required
              />
            </div>
          </div>
          <Button
            type="submit"
            className="w-full"
            data-testid="lead-submit-button"
            disabled={loading}
          >
            {submitLabel}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteConfirmDialog({
  open,
  onOpenChange,
  leadName,
  onConfirm,
  loading,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Delete Lead
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{leadName}</strong>? This
            action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LeadsPage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editLoading, setEditLoading] = useState(false);
  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);

  // Edit dialog
  const [editTarget, setEditTarget] = useState(null);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await apiClient.get("/leads");
      setLeads(response.data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    try {
      await apiClient.post("/leads", formData);
      toast.success("Lead created");
      setCreateOpen(false);
      fetchLeads();
    } catch {
      toast.error("Failed to create lead");
    }
  };

  const handleEdit = async (formData) => {
    setEditLoading(true);
    try {
      await apiClient.patch(`/leads/${editTarget.id}`, formData);
      toast.success("Lead updated");
      setEditTarget(null);
      fetchLeads();
    } catch {
      toast.error("Failed to update lead");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/leads/${deleteTarget.id}`);
      toast.success("Lead deleted");
      setDeleteTarget(null);
      fetchLeads();
    } catch {
      toast.error("Failed to delete lead");
    } finally {
      setDeleteLoading(false);
    }
  };

  const advanceStatus = async (lead) => {
    const action = nextAction[lead.status];
    if (!action) return;
    try {
      await apiClient.patch(`/leads/${lead.id}`, { status: action.next });
      toast.success(`Marked as ${statusConfig[action.next].label}`);
      fetchLeads();
    } catch {
      toast.error("Failed to update lead");
    }
  };

  const counts = STATUS_FLOW.reduce((acc, s) => {
    acc[s] = leads.filter((l) => l.status === s).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1
            className="text-3xl lg:text-4xl font-bold"
            style={{ letterSpacing: "-0.02em" }}
          >
            Leads
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {loading
              ? "Loading…"
              : `${leads.length} lead${leads.length !== 1 ? "s" : ""} total`}
          </p>
        </div>
        <Button
          data-testid="add-lead-button"
          className="w-full sm:w-auto"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Lead
        </Button>
      </div>

      {/* ── Pipeline Summary ── */}
      {!loading && leads.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {STATUS_FLOW.map((s) => {
            const cfg = statusConfig[s];
            return (
              <Card key={s} className="p-3 text-center border-0 shadow-sm">
                <span
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.badge}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </span>
                <p
                  className="text-2xl font-bold mt-1"
                  style={{ letterSpacing: "-0.02em" }}
                >
                  {counts[s] ?? 0}
                </p>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Lead Cards ── */}
      <div className="grid gap-3 fade-in">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <LeadCardSkeleton key={i} />)
        ) : leads.length === 0 ? (
          <Card className="py-16 text-center border-dashed">
            <Radio className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="font-medium text-muted-foreground">No leads yet</p>
            <p className="text-sm text-muted-foreground/60 mt-1">
              Add your first lead to get the pipeline going
            </p>
          </Card>
        ) : (
          leads.map((lead) => {
            const cfg = statusConfig[lead.status] ?? statusConfig.new;
            const action = nextAction[lead.status];
            return (
              <Card
                key={lead.id}
                className="p-5 lg:p-6 hover-lift"
                data-testid="lead-card"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-3">
                      <h3 className="text-base lg:text-lg font-semibold">
                        {lead.name}
                      </h3>
                      <Badge
                        className={`text-xs border flex items-center gap-1.5 ${cfg.badge}`}
                        data-testid="lead-status-badge"
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}
                        />
                        {cfg.label}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Briefcase className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate font-medium text-foreground/80">
                          {lead.business}
                        </span>
                      </span>
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-mono text-foreground/80">
                          {lead.phone}
                        </span>
                      </span>
                      <span className="flex items-center gap-2 text-muted-foreground truncate">
                        <Zap className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{lead.requirement}</span>
                      </span>
                      <span className="text-muted-foreground truncate">
                        <span className="text-xs uppercase tracking-wide mr-2 font-medium">
                          Source
                        </span>
                        {lead.source}
                      </span>
                    </div>

                    {/* Progress bar */}
                    <StatusProgressBar status={lead.status} />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 sm:flex-col sm:items-end lg:flex-row lg:items-center flex-wrap">
                    {action && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => advanceStatus(lead)}
                        data-testid={action.testId}
                        className="text-xs flex-1 sm:flex-none"
                      >
                        {action.label}
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => setEditTarget(lead)}
                      data-testid="edit-lead-button"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteTarget(lead)}
                      data-testid="delete-lead-button"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* ── Dialogs ── */}
      <LeadFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initial={EMPTY_FORM}
        onSubmit={handleCreate}
        loading={loading}
        title="Add New Lead"
        submitLabel="Create Lead"
      />

      <LeadFormDialog
        open={!!editTarget}
        onOpenChange={(v) => !v && setEditTarget(null)}
        initial={editTarget}
        onSubmit={handleEdit}
        loading={editLoading}
        title="Edit Lead"
        submitLabel="Save Changes"
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        leadName={deleteTarget?.name}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </div>
  );
}
